import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import { getCompanyNames } from '../../services/companyService';

const normalizeName = (name) => String(name || '').trim();

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: ${(p) => (p.$variant === 'details' ? '300px' : 'none')};
  flex: ${(p) => (p.$variant === 'details' ? '1' : 'none')};
`;

const Field = styled.div`
  display: flex;
  align-items: stretch;
  border: ${(p) => (p.$variant === 'details' ? '2px' : '1px')} solid var(--color-border-primary);
  border-radius: 8px;
  background: var(--color-bg-primary);
  overflow: hidden;
  min-height: ${(p) => (p.$variant === 'details' ? 'auto' : '40px')};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--color-border-secondary);
  }

  &:focus-within {
    border-color: ${(p) => (p.$variant === 'details' ? 'var(--color-secondary)' : 'var(--color-text-primary)')};
    box-shadow: ${(p) =>
      p.$variant === 'details' ? '0 0 0 2px rgba(178, 190, 195, 0.25)' : '0 0 0 3px var(--focus-ring)'};
  }
`;

const Input = styled.input`
  && {
    flex: 1;
    min-width: 0;
    width: auto;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
    color: var(--color-text-primary);
    font-family: inherit;
    font-size: ${(p) => (p.$variant === 'details' ? '0.95rem' : '0.875rem')};
    font-weight: ${(p) => (p.$variant === 'details' ? 500 : 400)};
    padding: ${(p) => (p.$variant === 'details' ? '8px 12px' : '0.5rem 0.75rem')};
    outline: none;
  }

  &&::placeholder {
    color: var(--color-text-muted);
  }

  &&:disabled {
    cursor: not-allowed;
    color: var(--color-text-secondary);
  }

  &&:focus {
    border: none;
    box-shadow: none;
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  border: none;
  border-left: 1px solid var(--color-border-primary);
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Menu = styled.ul`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Option = styled.li`
  padding: 0.45rem 0.75rem;
  font-size: 0.9rem;
  color: var(--color-text-primary);
  cursor: pointer;
  background: ${(p) => (p.$active ? 'var(--color-bg-secondary)' : 'transparent')};

  &:hover {
    background: var(--color-bg-secondary);
  }
`;

const Empty = styled.li`
  padding: 0.45rem 0.75rem;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const CompanyNameCombobox = ({
  id,
  value,
  onChange,
  placeholder = 'Nome da empresa',
  disabled = false,
  variant = 'form',
}) => {
  const { companyNames } = useApp();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [fetchedNames, setFetchedNames] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(companyNames) && companyNames.length > 0) return undefined;

    let cancelled = false;
    getCompanyNames()
      .then((names) => {
        if (!cancelled) {
          setFetchedNames(Array.isArray(names) ? names : []);
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedNames([]);
      });

    return () => {
      cancelled = true;
    };
  }, [companyNames]);

  const options = useMemo(() => {
    const source = companyNames?.length ? companyNames : fetchedNames;
    const unique = [];
    const seen = new Set();

    (source || []).forEach((name) => {
      const normalized = normalizeName(name);
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      unique.push(normalized);
    });

    return unique.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [companyNames, fetchedNames]);

  const query = normalizeName(value).toLowerCase();
  const filtered = useMemo(() => {
    if (!query) return options;
    return options.filter((name) => name.toLowerCase().includes(query));
  }, [options, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectName = (name) => {
    onChange(name);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((prev) => Math.max(prev - 1, 0));
    }

    if (event.key === 'Enter' && open && filtered[highlight]) {
      event.preventDefault();
      selectName(filtered[highlight]);
    }

    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <Wrapper ref={wrapperRef} $variant={variant}>
      <Field $variant={variant}>
        <Input
          id={id}
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          $variant={variant}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <Toggle
          type="button"
          aria-label={open ? 'Fechar lista de empresas' : 'Abrir lista de empresas'}
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </Toggle>
      </Field>

      {open && !disabled && (
        <Menu role="listbox">
          {filtered.length === 0 ? (
            <Empty>Nenhuma empresa na lista. O nome digitado será usado.</Empty>
          ) : (
            filtered.map((name, index) => (
              <Option
                key={name}
                role="option"
                aria-selected={index === highlight}
                $active={index === highlight}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectName(name)}
              >
                {name}
              </Option>
            ))
          )}
        </Menu>
      )}
    </Wrapper>
  );
};

CompanyNameCombobox.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['form', 'details']),
};

export default CompanyNameCombobox;
